import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Eye as EyeIcon, Download as DownloadIcon, X as XIcon } from 'lucide-react';
import type { FileAttachment } from '../../types/kataBoardV2';
import { fileAttachmentApi } from '../../services/kataBoardV2';

// Resizable Preview Modal Component
interface FileAttachmentPreviewModalProps {
  file: FileAttachment;
  onClose: () => void;
}

const FileAttachmentPreviewModal: React.FC<FileAttachmentPreviewModalProps> = ({ file, onClose }) => {
  const [modalSize, setModalSize] = useState(() => ({
    width: Math.min(900, window.innerWidth - 32),
    height: Math.min(700, window.innerHeight - 32),
  }));
  const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const previousFocus = document.activeElement as HTMLElement | null;
    modalRef.current?.querySelector<HTMLButtonElement>('button[title="Close preview"]')?.focus();
    return () => previousFocus?.focus();
  }, []);

  // Center modal on mount
  useEffect(() => {
    const centerModal = () => {
      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;
      setModalPosition({
        x: (windowWidth - modalSize.width) / 2,
        y: (windowHeight - modalSize.height) / 2
      });
    };
    centerModal();
  }, [modalSize.width, modalSize.height]);

  // Handle drag start
  const handleMouseDown = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('.resize-handle') || (e.target as HTMLElement).closest('button, a')) return;

    setIsDragging(true);
    setDragStart({
      x: e.clientX - modalPosition.x,
      y: e.clientY - modalPosition.y
    });
  };

  // Handle resize start
  const handleResizeMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsResizing(true);
    setResizeStart({
      x: e.clientX,
      y: e.clientY,
      width: modalSize.width,
      height: modalSize.height
    });
  };

  // Handle mouse move
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        setModalPosition({
          x: e.clientX - dragStart.x,
          y: e.clientY - dragStart.y
        });
      } else if (isResizing) {
        const newWidth = Math.max(400, resizeStart.width + (e.clientX - resizeStart.x));
        const newHeight = Math.max(300, resizeStart.height + (e.clientY - resizeStart.y));
        setModalSize({ width: newWidth, height: newHeight });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setIsResizing(false);
    };

    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, isResizing, dragStart, resizeStart]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[100]">
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-label={`Preview ${file.original_filename}`}
        className="absolute flex flex-col bg-white rounded-lg shadow-2xl overflow-hidden"
        style={{
          left: modalPosition.x,
          top: modalPosition.y,
          width: modalSize.width,
          height: modalSize.height,
          cursor: isDragging ? 'grabbing' : 'grab'
        }}
        onMouseDown={handleMouseDown}
      >
        {/* Header */}
        <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 p-4 border-b bg-gray-50 select-none">
          <div className="flex min-w-0 flex-1 items-center space-x-3">
            <span className="text-2xl">{fileAttachmentApi.getFileIcon(file.mime_type)}</span>
            <div className="min-w-0">
              <h3 className="truncate text-lg font-medium text-gray-900" title={file.original_filename}>
                {file.title || file.original_filename}
              </h3>
              <p className="text-sm text-gray-500">
                {(file.file_size / 1024 / 1024).toFixed(2)} MB • {file.mime_type}
              </p>
            </div>
          </div>
          <div className="flex shrink-0 items-center space-x-2">
            <a
              href={fileAttachmentApi.getDownloadUrl(file)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-3 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700"
              title="Open in new tab"
            >
              <EyeIcon className="h-4 w-4 mr-2" />
              Open
            </a>
            <a
              href={fileAttachmentApi.getDownloadUrl(file)}
              download={file.original_filename}
              className="inline-flex items-center px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700"
              title="Download file"
            >
              <DownloadIcon className="h-4 w-4 mr-2" />
              Download
            </a>
            <button
              type="button"
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100"
              title="Close preview"
            >
              <XIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="min-h-0 flex-1 overflow-hidden">
          {file.is_image ? (
            <div className="w-full h-full flex items-center justify-center bg-gray-100 p-4">
              <img
                src={fileAttachmentApi.getDownloadUrl(file)}
                alt={file.original_filename}
                className="max-w-full max-h-full object-contain rounded shadow-lg"
              />
            </div>
          ) : file.is_pdf ? (
            <div className="w-full h-full">
              <PDFPreview file={file} />
            </div>
          ) : (
            <div className="flex items-center justify-center h-full bg-gray-50">
              <div className="text-center py-8">
                <span className="text-8xl mb-6 block">{fileAttachmentApi.getFileIcon(file.mime_type)}</span>
                <h4 className="text-xl font-medium text-gray-900 mb-2">Preview not available</h4>
                <p className="text-sm text-gray-600 mb-6">This file type cannot be previewed in the browser</p>
                <div className="space-x-3">
                  <a
                    href={fileAttachmentApi.getDownloadUrl(file)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700"
                  >
                    <EyeIcon className="h-4 w-4 mr-2" />
                    Open in New Tab
                  </a>
                  <a
                    href={fileAttachmentApi.getDownloadUrl(file)}
                    download={file.original_filename}
                    className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700"
                  >
                    <DownloadIcon className="h-4 w-4 mr-2" />
                    Download File
                  </a>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Resize Handle */}
        <div
          className="resize-handle absolute bottom-0 right-0 w-4 h-4 cursor-se-resize"
          onMouseDown={handleResizeMouseDown}
        >
          <div className="absolute bottom-1 right-1 w-2 h-2 bg-gray-400 transform rotate-45"></div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

// Load protected PDFs through the same authenticated client as downloads.
const PDFPreview: React.FC<{ file: FileAttachment }> = ({ file }) => {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;
    setPdfUrl(null);
    setError(false);
    fileAttachmentApi.download(file.id).then(blob => {
      if (cancelled) return;
      if (blob.type !== 'application/pdf') throw new Error('Invalid PDF response');
      objectUrl = URL.createObjectURL(blob);
      setPdfUrl(objectUrl);
    }).catch(() => {
      if (!cancelled) setError(true);
    });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [file.id]);

  if (error) {
    return (
      <div role="alert" className="flex h-full items-center justify-center bg-gray-50 p-6 text-center">
        <div>
          <p className="text-sm text-gray-600">PDF preview not available. Use Open or Download to access this file.</p>
        </div>
      </div>
    );
  }
  if (!pdfUrl) {
    return (
      <div role="status" className="flex h-full items-center justify-center bg-gray-50">
        <p className="text-sm text-gray-600">Loading PDF preview...</p>
      </div>
    );
  }
  return (
    <object data={pdfUrl} type="application/pdf" title={file.original_filename} className="w-full h-full">
      <p className="p-6 text-sm text-gray-600">Your browser cannot display this PDF. Use Open or Download to access it.</p>
    </object>
  );
};

export default FileAttachmentPreviewModal;
